import React, { useState, useRef, useEffect } from "react";
import { 
  Stethoscope, 
  FlaskConical, 
  Pill, 
  LifeBuoy, 
  AlertTriangle, 
  CalendarClock, 
  ClipboardList, 
  BadgeInfo,
  Heart,
  Edit2,
  Check,
  X,
  PlusCircle,
  Trash2,
  Lock
} from "lucide-react";
import { authService } from "../../services/authService";
import axiosInstance from "../../config/axios";

interface MedicationProps {
  name: string;
  dosage: string;
  frequency: string;
  duration: string;
  purpose: string;
}

interface InvestigationProps {
  investigation: string;
  rationale?: string;
}

interface AIDoctorProps {
  aiDoctor?: {
    clinical_assessment?: string;
    recommended_investigations?: (string | InvestigationProps)[];
    treatment_plan?: {
      immediate_interventions?: string;
      medications?: MedicationProps[];
      long_term_management?: string;
    };
    lifestyle_recommendations?: string[];
    red_flags?: string[];
    follow_up?: string;
  };
  readOnly?: boolean;
  onSave?: (updatedAIDoctor: any) => void;
  recordingId?: string | number;
  patientId?: string | number;
  consultationDate?: string;
  metadata?: Record<string, any>;
}

export const AIDoctorView: React.FC<AIDoctorProps> = ({ 
  aiDoctor, 
  readOnly: initialReadOnly = false, 
  onSave,
  recordingId,
  patientId,
  consultationDate,
  metadata = {}
}) => {
  // Override read-only state
  const [forceEditMode, setForceEditMode] = useState(false);
  
  // Calculated read-only state that can be overridden
  const readOnly = initialReadOnly && !forceEditMode;
  
  // API saving state
  const [isSavingToApi, setIsSavingToApi] = useState(false);
  
  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  
  // Error notification state
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("An error occurred");
  
  // Auto-hide success notification after 5 seconds
  useEffect(() => {
    if (showSuccessNotification) {
      const timer = setTimeout(() => setShowSuccessNotification(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessNotification]);
  
  // Auto-hide error notification after 8 seconds
  useEffect(() => {
    if (showErrorNotification) {
      const timer = setTimeout(() => setShowErrorNotification(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showErrorNotification]);
  
  // Debug the readOnly prop
  console.log("AIDoctorView readOnly prop:", initialReadOnly, "Force edit mode:", forceEditMode, "Effective readOnly:", readOnly);
  
  // Edit mode states
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<any>(aiDoctor || {});
  
  // Temporary states for editing arrays
  const [newInvestigation, setNewInvestigation] = useState("");
  const [newInvestigationRationale, setNewInvestigationRationale] = useState("");
  const [newLifestyleRec, setNewLifestyleRec] = useState("");
  const [newRedFlag, setNewRedFlag] = useState("");
  const [editingMedication, setEditingMedication] = useState<MedicationProps | null>(null);
  const [editingMedicationIndex, setEditingMedicationIndex] = useState<number | null>(null);
  const [newMedication, setNewMedication] = useState<MedicationProps>({
    name: "",
    dosage: "",
    frequency: "",
    duration: "",
    purpose: ""
  });

  if (!aiDoctor && !editedContent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Stethoscope className="w-16 h-16 mb-4" />
        <h3 className="text-lg font-medium">No AI doctor insights available</h3>
        <p className="text-sm">AI analysis will appear here when available</p>
      </div>
    );
  }

  const handleEditToggle = (section: string) => {
    if (readOnly) return;
    
    if (editingSection === section) {
      setEditingSection(null);
      
      // Save changes when exiting edit mode
      if (onSave) {
        onSave(editedContent);
      }
    } else {
      setEditingSection(section);
    }
  };

  const handleTextChange = (path: string[], value: string | any[]) => {
    setEditedContent((prev: any) => {
      const result = { ...prev };
      let current = result;
      
      // Navigate the path except the last element
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      // Set the value at the last path element
      current[path[path.length - 1]] = value;
      return result;
    });
  };

  // Array item handlers
  const handleAddArrayItem = (path: string[], newItem: string | object) => {
    if (typeof newItem === 'string' && !newItem.trim()) return;
    
    setEditedContent((prev: any) => {
      const result = { ...prev };
      let current = result;
      
      // Navigate the path except the last element
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      // Add item to the array at the last path element
      const lastKey = path[path.length - 1];
      if (!current[lastKey]) {
        current[lastKey] = [];
      }
      
      current[lastKey] = [...current[lastKey], newItem];
      return result;
    });
  };

  const handleRemoveArrayItem = (path: string[], index: number) => {
    setEditedContent((prev: any) => {
      const result = { ...prev };
      let current = result;
      
      // Navigate the path except the last element
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
        if (!current) return prev; // Return unchanged if path is invalid
      }
      
      // Remove item from the array at the last path element
      const lastKey = path[path.length - 1];
      if (current[lastKey] && Array.isArray(current[lastKey])) {
        current[lastKey] = current[lastKey].filter((_: any, i: number) => i !== index);
      }
      
      return result;
    });
  };

  // New function to update an item in an array
  const handleUpdateArrayItem = (path: string[], index: number, value: string | any) => {
    setEditedContent((prev: any) => {
      const result = { ...prev };
      let current = result;
      
      // Navigate the path except the last element
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      // Update item in the array at the last path element
      const lastKey = path[path.length - 1];
      if (!current[lastKey]) {
        current[lastKey] = [];
      }
      
      const updatedArray = [...current[lastKey]];
      updatedArray[index] = value;
      current[lastKey] = updatedArray;
      
      return result;
    });
  };

  // Medication handlers
  const handleAddMedication = () => {
    if (!newMedication.name.trim()) return;
    
    setEditedContent((prev: any) => {
      const result = { ...prev };
      if (!result.treatment_plan) {
        result.treatment_plan = {};
      }
      if (!result.treatment_plan.medications) {
        result.treatment_plan.medications = [];
      }
      
      result.treatment_plan.medications = [
        ...result.treatment_plan.medications,
        { ...newMedication }
      ];
      
      return result;
    });
    
    // Reset form
    setNewMedication({
      name: "",
      dosage: "",
      frequency: "",
      duration: "",
      purpose: ""
    });
  };

  const handleEditMedication = (index: number) => {
    const medication = editedContent?.treatment_plan?.medications?.[index];
    if (medication) {
      setEditingMedication({ ...medication });
      setEditingMedicationIndex(index);
    }
  };

  const handleUpdateMedication = () => {
    if (editingMedicationIndex === null || !editingMedication) return;
    
    setEditedContent((prev: any) => {
      const result = { ...prev };
      if (result.treatment_plan?.medications && result.treatment_plan.medications[editingMedicationIndex]) {
        result.treatment_plan.medications[editingMedicationIndex] = { ...editingMedication };
      }
      return result;
    });
    
    // Reset editing state
    setEditingMedication(null);
    setEditingMedicationIndex(null);
  };

  const handleRemoveMedication = (index: number) => {
    setEditedContent((prev: any) => {
      const result = { ...prev };
      if (result.treatment_plan?.medications) {
        result.treatment_plan.medications = result.treatment_plan.medications.filter((_: any, i: number) => i !== index);
      }
      return result;
    });
  };

  // Function to save changes to API
  const saveToApi = async () => {
    if (!recordingId) {
      console.error("Cannot save changes: No recording ID provided");
      setErrorMessage("Cannot save changes: No recording ID provided");
      setShowErrorNotification(true);
      return;
    }
    
    try {
      setIsSavingToApi(true);
      
      // Prepare the payload according to the API requirements
      const payload = {
        data: {
          recordingId: String(recordingId),
          aiDoctor: editedContent
        }
      };
      
      console.log("Saving to API with payload:", payload);
      
      // Make the API call with the configured axios instance
      const response = await axiosInstance.put(
        `/api/scribing/speech/recordings/${recordingId}/transcript`,
        payload
      );
      
      console.log("API response:", response.data);
      
      // Success handling
      setShowSuccessNotification(true);
      setEditingSection(null);
      
      // Call the parent's onSave if provided
      if (onSave) {
        onSave({
          aiDoctor: editedContent
        });
      }
      
      // Turn off forced edit mode if it was on
      if (forceEditMode) {
        setForceEditMode(false);
      }
    } catch (error) {
      console.error("Failed to save to API:", error);
      setErrorMessage(error instanceof Error ? error.message : "Failed to save to API");
      setShowErrorNotification(true);
      // Keep the modal open to allow the user to try again
    } finally {
      setIsSavingToApi(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100">
              <Stethoscope className="text-indigo-600 w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Doctor Assessment</h2>
              <p className="text-sm text-gray-500">AI-powered clinical insights and recommendations</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {initialReadOnly && (
              <button
                onClick={() => setForceEditMode(!forceEditMode)}
                className={`px-4 py-2 ${forceEditMode ? 'bg-amber-600' : 'bg-indigo-600'} text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:${forceEditMode ? 'bg-amber-700' : 'bg-indigo-700'} transition-colors`}
              >
                {forceEditMode ? (
                  <>
                    <Lock className="w-4 h-4" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Edit2 className="w-4 h-4" />
                     Edit
                  </>
                )}
              </button>
            )}
            {!readOnly && onSave && (
              <button
                onClick={saveToApi}
                disabled={isSavingToApi}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isSavingToApi ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save All Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Clinical Assessment */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <BadgeInfo className="text-indigo-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Clinical Assessment</h3>
            </div>
            {!readOnly && (
              <button 
                onClick={() => handleEditToggle('clinical_assessment')}
                className="p-2 rounded-full hover:bg-indigo-200/50 transition-colors"
              >
                {editingSection === 'clinical_assessment' ? (
                  <Check className="w-4 h-4 text-indigo-600" />
                ) : (
                  <Edit2 className="w-4 h-4 text-indigo-600" />
                )}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingSection === 'clinical_assessment' ? (
              <textarea
                value={editedContent?.clinical_assessment || ''}
                onChange={(e) => handleTextChange(['clinical_assessment'], e.target.value)}
                className="w-full h-40 p-3 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none text-gray-700 leading-relaxed"
                placeholder="Enter clinical assessment..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {editedContent?.clinical_assessment || 'No clinical assessment available.'}
              </p>
            )}
          </div>
        </div>

        {/* Recommended Investigations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FlaskConical className="text-blue-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Recommended Investigations</h3>
            </div>
            {!readOnly && (
              <button 
                onClick={() => handleEditToggle('recommended_investigations')}
                className="p-2 rounded-full hover:bg-blue-200/50 transition-colors"
              >
                {editingSection === 'recommended_investigations' ? (
                  <Check className="w-4 h-4 text-blue-600" />
                ) : (
                  <Edit2 className="w-4 h-4 text-blue-600" />
                )}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingSection === 'recommended_investigations' ? (
              <div className="space-y-4">
                <ul className="space-y-3">
                  {(editedContent?.recommended_investigations || []).map((investigation: string | InvestigationProps, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-blue-100 text-blue-600 shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={typeof investigation === 'string' ? investigation : investigation.investigation}
                            onChange={(e) => {
                              if (typeof investigation === 'string') {
                                handleUpdateArrayItem(['recommended_investigations'], index, e.target.value);
                              } else {
                                handleUpdateArrayItem(['recommended_investigations'], index, {
                                  ...investigation,
                                  investigation: e.target.value
                                });
                              }
                            }}
                            className="flex-1 p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700"
                          />
                          <button 
                            onClick={() => handleRemoveArrayItem(['recommended_investigations'], index)}
                            className="p-1 rounded-full hover:bg-red-100 text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Display rationale input if it's an object with rationale */}
                        {typeof investigation !== 'string' && (
                          <div className="flex items-center gap-2 ml-2">
                            <span className="text-xs text-gray-500">Rationale:</span>
                            <input
                              type="text"
                              value={investigation.rationale || ''}
                              onChange={(e) => {
                                handleUpdateArrayItem(['recommended_investigations'], index, {
                                  ...investigation,
                                  rationale: e.target.value
                                });
                              }}
                              className="flex-1 p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700 text-sm"
                              placeholder="Add rationale (optional)"
                            />
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                
                <div className="space-y-2 mt-4">
                  <input
                    type="text"
                    value={newInvestigation}
                    onChange={(e) => setNewInvestigation(e.target.value)}
                    placeholder="Add new investigation..."
                    className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700"
                  />
                  
                  <input
                    type="text"
                    value={newInvestigationRationale}
                    onChange={(e) => setNewInvestigationRationale(e.target.value)}
                    placeholder="Rationale (optional)..."
                    className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700"
                  />
                  
                  <div className="flex justify-end">
                    <button 
                      onClick={() => {
                        if (newInvestigation.trim()) {
                          if (newInvestigationRationale.trim()) {
                            // Add as an object with rationale
                            handleAddArrayItem(['recommended_investigations'], {
                              investigation: newInvestigation,
                              rationale: newInvestigationRationale
                            });
                          } else {
                            // Add as a simple string
                            handleAddArrayItem(['recommended_investigations'], newInvestigation);
                          }
                          setNewInvestigation('');
                          setNewInvestigationRationale('');
                        }
                      }}
                      disabled={!newInvestigation.trim()}
                      className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {(editedContent?.recommended_investigations || []).length > 0 ? (
                  (editedContent?.recommended_investigations || []).map((investigation: string | InvestigationProps, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      {typeof investigation === 'string' ? (
                        // Simple string investigation
                        <>
                          <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-blue-100 text-blue-600 shrink-0">
                            {index + 1}
                          </div>
                          <div className="text-gray-700">{investigation}</div>
                        </>
                      ) : (
                        // Complex investigation object with potential rationale
                        <div className="w-full bg-white border border-blue-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                          <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center gap-2">
                            <div className="p-1 bg-blue-100 rounded-full">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 2L13.8 10.2"></path>
                                <path d="m22 2-7 20-4-9-9-4 20-7Z"></path>
                              </svg>
                            </div>
                            <h4 className="font-medium text-gray-800">{investigation.investigation}</h4>
                          </div>
                          {investigation.rationale && (
                            <div className="px-4 py-3">
                              <p className="text-sm text-gray-600">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rationale:</span>
                                <span className="block mt-1 leading-relaxed">{investigation.rationale}</span>
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500 italic">No recommended investigations available.</li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Treatment Plan */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-teal-50 to-teal-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ClipboardList className="text-teal-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Treatment Plan</h3>
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                <button 
                  onClick={() => handleEditToggle('treatment_plan_interventions')}
                  className="p-2 rounded-full hover:bg-teal-200/50 transition-colors"
                  title="Edit Immediate Interventions"
                >
                  {editingSection === 'treatment_plan_interventions' ? (
                    <Check className="w-4 h-4 text-teal-600" />
                  ) : (
                    <Edit2 className="w-4 h-4 text-teal-600" />
                  )}
                </button>
              </div>
            )}
          </div>
          <div className="p-6 space-y-6">
            {/* Immediate Interventions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Immediate Interventions</h4>
                {!readOnly && editingSection !== 'treatment_plan_interventions' && (
                  <button 
                    onClick={() => handleEditToggle('treatment_plan_interventions')}
                    className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {editingSection === 'treatment_plan_interventions' ? (
                <textarea
                  value={editedContent?.treatment_plan?.immediate_interventions || ''}
                  onChange={(e) => handleTextChange(['treatment_plan', 'immediate_interventions'], e.target.value)}
                  className="w-full h-32 p-3 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all resize-none text-gray-700 leading-relaxed"
                  placeholder="Enter immediate interventions..."
                />
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  {editedContent?.treatment_plan?.immediate_interventions || 'No immediate interventions specified.'}
                </p>
              )}
            </div>

            {/* Medications */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Medications</h4>
                {!readOnly && (
                  <button 
                    onClick={() => handleEditToggle('treatment_plan_medications')}
                    className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                  >
                    {editingSection === 'treatment_plan_medications' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Edit2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              {editingSection === 'treatment_plan_medications' ? (
                <div className="space-y-6">
                  {/* List of existing medications */}
                  {(editedContent?.treatment_plan?.medications || []).map((medication: MedicationProps, index: number) => (
                    <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-teal-50 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Pill className="text-teal-600 w-4 h-4" />
                          <h5 className="font-medium text-gray-900">{medication.name}</h5>
                        </div>
                        <div className="flex items-center gap-1">
                          {editingMedicationIndex === index ? (
                            <button 
                              onClick={handleUpdateMedication}
                              className="p-1 rounded-full hover:bg-teal-200/50 text-teal-600"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleEditMedication(index)}
                              className="p-1 rounded-full hover:bg-teal-200/50 text-teal-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {editingMedicationIndex === index ? (
                            <button 
                              onClick={() => {
                                setEditingMedication(null);
                                setEditingMedicationIndex(null);
                              }}
                              className="p-1 rounded-full hover:bg-red-100 text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleRemoveMedication(index)}
                              className="p-1 rounded-full hover:bg-red-100 text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {editingMedicationIndex === index && editingMedication ? (
                        <div className="p-4 space-y-4 bg-teal-50/30 border-t border-teal-100">
                          <div>
                            <label className="text-sm text-gray-500 mb-1 block">Name</label>
                            <input
                              type="text"
                              value={editingMedication.name}
                              onChange={(e) => setEditingMedication({...editingMedication, name: e.target.value})}
                              className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-gray-500 mb-1 block">Dosage</label>
                              <input
                                type="text"
                                value={editingMedication.dosage}
                                onChange={(e) => setEditingMedication({...editingMedication, dosage: e.target.value})}
                                className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-500 mb-1 block">Frequency</label>
                              <input
                                type="text"
                                value={editingMedication.frequency}
                                onChange={(e) => setEditingMedication({...editingMedication, frequency: e.target.value})}
                                className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-gray-500 mb-1 block">Duration</label>
                              <input
                                type="text"
                                value={editingMedication.duration}
                                onChange={(e) => setEditingMedication({...editingMedication, duration: e.target.value})}
                                className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-500 mb-1 block">Purpose</label>
                              <input
                                type="text"
                                value={editingMedication.purpose}
                                onChange={(e) => setEditingMedication({...editingMedication, purpose: e.target.value})}
                                className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 mb-1">Dosage</p>
                            <p className="text-gray-700">{medication.dosage}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Frequency</p>
                            <p className="text-gray-700">{medication.frequency}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Duration</p>
                            <p className="text-gray-700">{medication.duration}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Purpose</p>
                            <p className="text-gray-700">{medication.purpose}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add new medication form */}
                  <div className="bg-white rounded-lg border border-dashed border-teal-300 overflow-hidden p-4">
                    <h5 className="font-medium text-teal-800 mb-4">Add New Medication</h5>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-500 mb-1 block">Name</label>
                        <input
                          type="text"
                          value={newMedication.name}
                          onChange={(e) => setNewMedication({...newMedication, name: e.target.value})}
                          placeholder="Medication name"
                          className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500 mb-1 block">Dosage</label>
                          <input
                            type="text"
                            value={newMedication.dosage}
                            onChange={(e) => setNewMedication({...newMedication, dosage: e.target.value})}
                            placeholder="e.g., 500mg"
                            className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-500 mb-1 block">Frequency</label>
                          <input
                            type="text"
                            value={newMedication.frequency}
                            onChange={(e) => setNewMedication({...newMedication, frequency: e.target.value})}
                            placeholder="e.g., Twice daily"
                            className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500 mb-1 block">Duration</label>
                          <input
                            type="text"
                            value={newMedication.duration}
                            onChange={(e) => setNewMedication({...newMedication, duration: e.target.value})}
                            placeholder="e.g., 10 days"
                            className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-500 mb-1 block">Purpose</label>
                          <input
                            type="text"
                            value={newMedication.purpose}
                            onChange={(e) => setNewMedication({...newMedication, purpose: e.target.value})}
                            placeholder="e.g., Pain relief"
                            className="w-full p-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={handleAddMedication}
                          disabled={!newMedication.name.trim()}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Add Medication
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(editedContent?.treatment_plan?.medications || []).length > 0 ? (
                    (editedContent?.treatment_plan?.medications || []).map((medication: MedicationProps, index: number) => (
                      <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 bg-teal-50 border-b border-gray-200 flex items-center gap-2">
                          <Pill className="text-teal-600 w-4 h-4" />
                          <h5 className="font-medium text-gray-900">{medication.name}</h5>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 mb-1">Dosage</p>
                            <p className="text-gray-700">{medication.dosage}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Frequency</p>
                            <p className="text-gray-700">{medication.frequency}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Duration</p>
                            <p className="text-gray-700">{medication.duration}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Purpose</p>
                            <p className="text-gray-700">{medication.purpose}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No medications prescribed.</p>
                  )}
                </div>
              )}
            </div>

            {/* Long Term Management */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Long Term Management</h4>
                {!readOnly && (
                  <button 
                    onClick={() => handleEditToggle('treatment_plan_long_term')}
                    className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                  >
                    {editingSection === 'treatment_plan_long_term' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Edit2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              {editingSection === 'treatment_plan_long_term' ? (
                <textarea
                  value={editedContent?.treatment_plan?.long_term_management || ''}
                  onChange={(e) => handleTextChange(['treatment_plan', 'long_term_management'], e.target.value)}
                  className="w-full h-32 p-3 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all resize-none text-gray-700 leading-relaxed"
                  placeholder="Enter long term management plan..."
                />
              ) : (
                <p className="text-gray-700 leading-relaxed">
                  {editedContent?.treatment_plan?.long_term_management || 'No long term management plan specified.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Lifestyle Recommendations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-green-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Heart className="text-green-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Lifestyle Recommendations</h3>
            </div>
            {!readOnly && (
              <button 
                onClick={() => handleEditToggle('lifestyle_recommendations')}
                className="p-2 rounded-full hover:bg-green-200/50 transition-colors"
              >
                {editingSection === 'lifestyle_recommendations' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Edit2 className="w-4 h-4 text-green-600" />
                )}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingSection === 'lifestyle_recommendations' ? (
              <div className="space-y-4">
                <ul className="space-y-3">
                  {(editedContent?.lifestyle_recommendations || []).map((recommendation: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-green-100 text-green-600 shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={recommendation}
                          onChange={(e) => {
                            handleUpdateArrayItem(['lifestyle_recommendations'], index, e.target.value);
                          }}
                          className="flex-1 p-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-gray-700"
                        />
                        <button 
                          onClick={() => handleRemoveArrayItem(['lifestyle_recommendations'], index)}
                          className="p-1 rounded-full hover:bg-red-100 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                
                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="text"
                    value={newLifestyleRec}
                    onChange={(e) => setNewLifestyleRec(e.target.value)}
                    placeholder="Add new recommendation..."
                    className="flex-1 p-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-gray-700"
                  />
                  <button 
                    onClick={() => {
                      handleAddArrayItem(['lifestyle_recommendations'], newLifestyleRec);
                      setNewLifestyleRec('');
                    }}
                    disabled={!newLifestyleRec.trim()}
                    className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {(editedContent?.lifestyle_recommendations || []).length > 0 ? (
                  (editedContent?.lifestyle_recommendations || []).map((recommendation: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-green-100 text-green-600 shrink-0">
                        {index + 1}
                      </div>
                      <div className="text-gray-700">{recommendation}</div>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500 italic">No lifestyle recommendations available.</li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Red Flags */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Red Flags</h3>
            </div>
            {!readOnly && (
              <button 
                onClick={() => handleEditToggle('red_flags')}
                className="p-2 rounded-full hover:bg-red-200/50 transition-colors"
              >
                {editingSection === 'red_flags' ? (
                  <Check className="w-4 h-4 text-red-600" />
                ) : (
                  <Edit2 className="w-4 h-4 text-red-600" />
                )}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingSection === 'red_flags' ? (
              <div className="space-y-4">
                <ul className="space-y-3">
                  {(editedContent?.red_flags || []).map((flag: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-red-100 text-red-600 shrink-0">
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={flag}
                          onChange={(e) => {
                            handleUpdateArrayItem(['red_flags'], index, e.target.value);
                          }}
                          className="flex-1 p-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-gray-700"
                        />
                        <button 
                          onClick={() => handleRemoveArrayItem(['red_flags'], index)}
                          className="p-1 rounded-full hover:bg-red-100 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                
                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="text"
                    value={newRedFlag}
                    onChange={(e) => setNewRedFlag(e.target.value)}
                    placeholder="Add new red flag warning..."
                    className="flex-1 p-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-gray-700"
                  />
                  <button 
                    onClick={() => {
                      handleAddArrayItem(['red_flags'], newRedFlag);
                      setNewRedFlag('');
                    }}
                    disabled={!newRedFlag.trim()}
                    className="p-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {(editedContent?.red_flags || []).length > 0 ? (
                  (editedContent?.red_flags || []).map((flag: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-red-100 text-red-600 shrink-0">
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div className="text-gray-700">{flag}</div>
                    </li>
                  ))
                ) : (
                  <li className="text-gray-500 italic">No red flags identified.</li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Follow Up */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-orange-100 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CalendarClock className="text-orange-600 w-5 h-5" />
              <h3 className="text-lg font-medium text-gray-900">Follow Up</h3>
            </div>
            {!readOnly && (
              <button 
                onClick={() => handleEditToggle('follow_up')}
                className="p-2 rounded-full hover:bg-orange-200/50 transition-colors"
              >
                {editingSection === 'follow_up' ? (
                  <Check className="w-4 h-4 text-orange-600" />
                ) : (
                  <Edit2 className="w-4 h-4 text-orange-600" />
                )}
              </button>
            )}
          </div>
          <div className="p-6">
            {editingSection === 'follow_up' ? (
              <textarea
                value={editedContent?.follow_up || ''}
                onChange={(e) => handleTextChange(['follow_up'], e.target.value)}
                className="w-full h-32 p-3 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none text-gray-700 leading-relaxed"
                placeholder="Enter follow up instructions..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">
                {editedContent?.follow_up || 'No follow up instructions provided.'}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-right-10 duration-300">
          <div className="bg-green-600 rounded-full p-1.5 text-white">
            <Check className="w-4 h-4" />
          </div>
          <div>
            <p className="font-medium">Successfully saved!</p>
            <p className="text-sm">Changes have been saved</p>
          </div>
          <button 
            onClick={() => setShowSuccessNotification(false)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Error Notification */}
      {showErrorNotification && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-600 text-red-800 p-4 rounded shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-right-10 duration-300">
          <div className="bg-red-600 rounded-full p-1.5 text-white">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{errorMessage}</p>
          </div>
          <button 
            onClick={() => setShowErrorNotification(false)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}; 